import React, { useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    TextField,
    Tooltip,
    CircularProgress,
    LinearProgress,
    Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { formatDate } from '../../utils/dateUtils';
import { useObraCronograma } from '../../hooks/useObraCronograma';
import { CronogramaItem } from '../../services/types';

interface ObraCronogramaTabProps {
    obraId: number;
}

export const ObraCronogramaTab: React.FC<ObraCronogramaTabProps> = ({ obraId }) => {
    const { cronograma, loadingCronograma, errorCronograma, updateCronograma } = useObraCronograma(String(obraId));
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValues, setEditValues] = useState<{
        data_inicio: string;
        data_fim: string;
        percentual_conclusao: string;
        status: string;
    }>({ data_inicio: '', data_fim: '', percentual_conclusao: '', status: '' });

    if (loadingCronograma) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }

    if (errorCronograma) {
        return <Typography color="error">Erro ao carregar cronograma: {(errorCronograma as Error).message}</Typography>;
    }

    const handleEditClick = (item: CronogramaItem) => {
        setEditingId(item.fase_id);
        setEditValues({
            data_inicio: item.data_inicio || '',
            data_fim: item.data_fim || '',
            percentual_conclusao: item.percentual_conclusao.toString(),
            status: item.status
        });
    };

    const handleCancelClick = () => {
        setEditingId(null);
    };

    const handleSaveClick = async (faseId: number) => {
        try {
            await updateCronograma({
                obra_id: obraId,
                fase_id: faseId,
                data_inicio: editValues.data_inicio || null,
                data_fim: editValues.data_fim || null,
                percentual_conclusao: parseFloat(editValues.percentual_conclusao) || 0,
                status: editValues.status
            });
            setEditingId(null);
        } catch (error) {
            console.error("Erro ao salvar cronograma:", error);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Concluído': return 'success';
            case 'Em Andamento': return 'primary';
            case 'Atrasado': return 'error';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Cronograma Físico-Financeiro</Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Fase</TableCell>
                            <TableCell>Data Início</TableCell>
                            <TableCell>Data Fim</TableCell>
                            <TableCell align="center">% Conclusão</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cronograma?.map((item) => (
                            <TableRow key={item.fase_id}>
                                <TableCell>{item.fase_nome}</TableCell>
                                <TableCell>
                                    {editingId === item.fase_id ? (
                                        <TextField
                                            type="date"
                                            size="small"
                                            value={editValues.data_inicio}
                                            onChange={(e) => setEditValues({ ...editValues, data_inicio: e.target.value })}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ max: "9999-12-31" }}
                                        />
                                    ) : (
                                        formatDate(item.data_inicio || '') || '-'
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editingId === item.fase_id ? (
                                        <TextField
                                            type="date"
                                            size="small"
                                            value={editValues.data_fim}
                                            onChange={(e) => setEditValues({ ...editValues, data_fim: e.target.value })}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ max: "9999-12-31" }}
                                        />
                                    ) : (
                                        formatDate(item.data_fim || '') || '-'
                                    )}
                                </TableCell>
                                <TableCell align="center" sx={{ width: '20%' }}>
                                    {editingId === item.fase_id ? (
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={editValues.percentual_conclusao}
                                            onChange={(e) => setEditValues({ ...editValues, percentual_conclusao: e.target.value })}
                                            inputProps={{ min: 0, max: 100 }}
                                        />
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ width: '100%', mr: 1 }}>
                                                <LinearProgress variant="determinate" value={item.percentual_conclusao} />
                                            </Box>
                                            <Box sx={{ minWidth: 35 }}>
                                                <Typography variant="body2" color="text.secondary">{`${Math.round(item.percentual_conclusao)}%`}</Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {editingId === item.fase_id ? (
                                        <TextField
                                            select
                                            size="small"
                                            value={editValues.status}
                                            onChange={(e) => setEditValues({ ...editValues, status: e.target.value })}
                                            SelectProps={{ native: true }}
                                        >
                                            <option value="Não Iniciado">Não Iniciado</option>
                                            <option value="Em Andamento">Em Andamento</option>
                                            <option value="Concluído">Concluído</option>
                                            <option value="Atrasado">Atrasado</option>
                                        </TextField>
                                    ) : (
                                        <Chip label={item.status} color={getStatusColor(item.status) as any} size="small" />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {editingId === item.fase_id ? (
                                        <>
                                            <IconButton onClick={() => handleSaveClick(item.fase_id)} color="primary" size="small">
                                                <SaveIcon />
                                            </IconButton>
                                            <IconButton onClick={handleCancelClick} color="error" size="small">
                                                <CancelIcon />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <Tooltip title="Editar Cronograma">
                                            <IconButton onClick={() => handleEditClick(item)} size="small">
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
